<script setup lang="ts">
import type { ContactFormModalProps, ContactMethod } from '.';
import type { OtpProvider } from '../LoginForm';
import type { ContactFormSectionExpose } from '~/components/ContactFormSection';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import {
	useContactFormActions,
	useContactFormEvents,
	useContactFormModal,
	useLeadFlowLogger
} from '~/composables/contactForm';
import { useEvents } from '~/composables/events/useEvents';
import { useContactFormExternalHandler } from '~/composables/useContactFormExternalHandler';
import { extractErrorLogContext } from '~/composables/useLogger';
import { useTypeformPopup } from '~/composables/useTypeform';
import {
	mailSouPilar,
	pilarFormattedPhone,
	pilarFormattedPhoneWhatsAppLink
} from '~/constants';
import { propertyTypesTranslations } from '~/core/entities/CRMEnums';
import { formatOpenHouseSchedule } from '~/core/entities/property/OpenHouse';
import { EventBusKeys } from '~/core/helpers/symbols';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cn } from '~/utils';
import { createLocaleTranslator } from '~/utils/localeMessages';
import { slugify } from '~~/utils';

import { ContactFormSection } from '~/components/ContactFormSection';
import OTPForm from '~/components/LoginForm/Steps/ThirdStep/Step.vue';
import { PrivacyTermsStatement } from '~/components/PrivacyTermsStatement';
import { DialogTitle } from '~/components/ui/dialog';
import Tag from '~/components/ui/tag/Tag.vue';
import TextLink from '~/components/ui/text-link/TextLink.vue';

import IconLineMdLoadingTwotoneLoop from '~icons/line-md/loading-twotone-loop';
import CheckIcon from '~icons/material-symbols-light/check';
import ChecklistIcon from '~icons/material-symbols-light/checklist';
import CloseIcon from '~icons/material-symbols-light/close';
import NorthEastIcon from '~icons/material-symbols-light/north-east';
import ScheduleIcon from '~icons/material-symbols-light/schedule-outline';
import WestIcon from '~icons/material-symbols-light/west';
import IconMaterialSymbolsCallOutline from '~icons/material-symbols/call-outline';
import CheckCircleIcon from '~icons/material-symbols/check-circle';
import DoneIcon from '~icons/material-symbols/done';
import ErrorCircleIcon from '~icons/material-symbols/error';
import IconMaterialSymbolsMailOutline from '~icons/material-symbols/mail-outline';
import RecordVoiceOverIcon from '~icons/material-symbols/record-voice-over-outline';

import propertyPricesLocale from './ContactFormModal.locale.json';
import OpenHouseSuccessMessage from './OpenHouseSuccessMessage.vue';
import OpenHouseVisitSummary from './OpenHouseVisitSummary.vue';

const props = withDefaults(defineProps<ContactFormModalProps>(), {
	context: 'property'
});

const strings = propertyPricesLocale.pt;
const openHousePrivacyMessages = {
	pt: {
		privacyPolicyStatement: strings.openHouse.privacyPolicyStatement,
		termsOfUseLabel: strings.termsOfUseLabel,
		privacyPolicyLabel: strings.privacyPolicyLabel
	}
};
const openHouseMobilePrivacyMessages = {
	pt: {
		...openHousePrivacyMessages.pt,
		privacyPolicyStatement: strings.openHouse.mobilePrivacyPolicyStatement
	}
};

const { translate, translateByContext } = createLocaleTranslator(
	strings as Record<string, unknown>,
	props.context
);

const router = useRouter();
const isOpen = defineModel<boolean>();
const isOpenHouseContext = computed(
	() => props.context === 'property' && !!props.openHouse
);
const openHouseSchedule = computed(() =>
	isOpenHouseContext.value && props.openHouse
		? formatOpenHouseSchedule(props.openHouse)
		: ''
);
const openHouseLocation = computed(() =>
	[
		props.propertyRegion,
		[props.propertyCity, props.propertyState].filter(Boolean).join(' - ')
	]
		.filter(Boolean)
		.join(', ')
);

const emit = defineEmits<{
	'scroll-to-similar-properties': [];
}>();

const modalState = useContactFormModal(props, { t: translate });

const actions = useContactFormActions({
	...modalState,
	props
});

const events = useContactFormEvents({ ...modalState, props, isOpen });

const logger = useLeadFlowLogger('ContactFormModal');

const { leadSatisfactionTypeformId } = useRuntimeConfig().public;
const { openTypeform: openLeadSurveyTypeform } = useTypeformPopup(
	leadSatisfactionTypeformId
);

function handleModalClose(shouldScroll: boolean = false) {
	isOpen.value = false;

	if (shouldScroll) {
		nextTick(() => {
			emit('scroll-to-similar-properties');
		});
	}
}

function handleCloseButtonClick(event: Event) {
	const target = event.target as HTMLElement;
	const button = target.closest('button');

	if (button && isOpen.value) {
		const srOnlySpan = button.querySelector('span.sr-only');
		if (srOnlySpan?.textContent?.includes('Close')) {
			event.preventDefault();
			event.stopPropagation();
			const shouldScroll = modalState.currentStep.value === 'success-message';
			handleModalClose(shouldScroll);
		}
	}
}

useEventListener(document, 'click', handleCloseButtonClick, { capture: true });

const handleContactMethodClick = (link: ContactMethod) => {
	events.handleContactMethodClick(link);
};

const wasOpenedByExternalHandler = ref(false);

const { user: nuxtAuthUser } = useUserSession();

useContactFormExternalHandler({
	isOpen: isOpen as Ref<boolean>,
	currentStep: modalState.currentStep,
	user: nuxtAuthUser,
	isUserLoggedIn: modalState.isUserLoggedIn,
	goToOtpFormStep: actions.goToOtpFormStep,
	sendForm: actions.sendForm,
	submitExternalForm: actions.submitExternalForm,
	addEventError: events.addEventError
});

onClientEventBus(useEventBus<void>(EventBusKeys.openContactModalAtOtp), () => {
	wasOpenedByExternalHandler.value = true;
});

const contactMethodProps = [
	{
		label: mailSouPilar,
		route: `mailto:${mailSouPilar}`,
		icon: IconMaterialSymbolsMailOutline
	},
	{
		label: pilarFormattedPhone,
		route: pilarFormattedPhoneWhatsAppLink,
		icon: IconMaterialSymbolsCallOutline
	}
];

const contactFormSection = useTemplateRef<ContactFormSectionExpose | null>(
	'contactFormSection'
);
const otpFormRef = useTemplateRef('otpFormRef');

async function handleFormSubmit() {
	try {
		contactFormSection.value?.syncAutofilledFields();
		await actions.sendForm();
	} catch (error) {
		const errorContext = {
			context: props.context,
			propertyId: props.propertyId,
			condoId: props.condoId,
			currentStep: modalState.currentStep.value,
			isUserLoggedIn: modalState.isUserLoggedIn.value
		};
		logger.error('Erro não tratado ao submeter formulário de contato', {
			...errorContext,
			...extractErrorLogContext(error)
		});
		logger.rumError(error, { ...errorContext, stage: 'modal_submit' });
		actions.goToErrorMessage((error as Error)?.message);
	}
}

function closeModal() {
	isOpen.value = false;
}

function closeModalWithScroll() {
	handleModalClose(true);
}

function goToContactFormStep() {
	actions.goToContactFormStep();
	events.addEventReturnClicked();

	nextTick(async () => {
		if (contactFormSection.value) {
			contactFormSection.value.validateFirstEmptyFieldToFocus();
		}
	});
}

const { trackCtaViewMoreClicked } = useEvents();

const goToSimilarPropertiesOnListing = () => {
	if (!props.locationSlug) {
		closeModalWithScroll();
		return;
	}

	const propertyTypeEnum = Object.entries(propertyTypesTranslations).find(
		([_, name]) => name === props.propertyTypeName
	)?.[0] as PropertyTypeEnum | undefined;

	const newFilters = {
		maxAskingPrice: props.askingPrice ?? undefined,
		propertyTypes: propertyTypeEnum ? [propertyTypeEnum] : undefined,
		locationSlug: slugify(props.locationSlug ?? '')
	};

	const url = PropertyFiltersPresenter.getPathFromFilters(
		newFilters,
		{},
		{
			page: 'imoveis'
		}
	);

	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_FLOW]: 'lead form',
		[MixpanelProps.CURRENT_PAGE]: modalState.currentPageName.value,
		[MixpanelProps.CTA_TEXT]: isOpenHouseContext.value
			? strings.openHouse.similarProperties
			: strings.similarProperties,
		[MixpanelProps.DESTINATION_URL]: url
	});

	router.push(url);
};

function handleFormDataUpdate(
	updatedData: Partial<{
		name: string;
		phone: string;
		email: string;
		userMessage: string;
	}>
) {
	if ('name' in updatedData) {
		const value = updatedData.name?.trim() ?? '';
		modalState.setFieldValue('name', value);
	}
	if ('phone' in updatedData) {
		const value = updatedData.phone?.trim() ?? '';
		modalState.setFieldValue('phone', value);
	}
	if ('email' in updatedData) {
		const value = updatedData.email?.trim() ?? '';
		modalState.setFieldValue('email', value);
	}
	if ('userMessage' in updatedData) {
		const value = updatedData.userMessage?.trim() ?? '';
		modalState.setFieldValue('userMessage', value);
	}
}

const modalTitle = computed<string>(() => {
	const i18nOptions = { brokerName: modalState.brokerFirstName.value };

	if (modalState.currentStep.value === 'success-message') {
		return strings.successTitle;
	}

	if (modalState.currentStep.value === 'submit-error-message') {
		return strings.errorTitle;
	}

	if (modalState.currentStep.value === 'otp-form') {
		return strings.phoneOtpTitle;
	}

	if (isOpenHouseContext.value) {
		return strings.openHouse.title;
	}

	return modalState.isUserLoggedIn.value
		? translateByContext('titleForLoggedUser', i18nOptions)
		: translateByContext('titleForUnloggedUser', i18nOptions);
});

const getImageSrc = useImage();
const brokerSrc = computed(() =>
	getImageSrc(props.broker?.profilePicture?.croppedUrl || '', {
		format: 'webp',
		width: 128
	})
);

const validateFirstEmptyFieldToFocus = async () => {
	await nextTick();
	if (contactFormSection.value) {
		await contactFormSection.value.validateFirstEmptyFieldToFocus();
	}
};

watch(isOpen, (newValue, oldValue) => {
	if (newValue || !oldValue) return;

	events.addEventsCloseModal();
});

watch(
	isOpen,
	isOpen => {
		if (!isOpen) actions.cancelPendingSubmission();
	},
	{ flush: 'sync' }
);

watch(
	modalState.initialUserMessage,
	message => modalState.setFieldValue('userMessage', message, false),
	{
		immediate: true
	}
);

watch(
	[() => isOpen.value, () => modalState.user.value],
	async () => {
		if (!isOpen.value || !modalState.user.value) return;
		const userData = modalState.user.value;

		if (!userData) return;

		if (!modalState.name.value)
			modalState.setFieldValue('name', userData.name ?? '', false);
		if (!modalState.phone.value)
			modalState.setFieldValue(
				'phone',
				modalState.getUserFulfilledPhone(),
				false
			);
		if (!modalState.email.value)
			modalState.setFieldValue('email', userData.email ?? '', false);
		if (!modalState.userMessage.value) {
			modalState.setFieldValue(
				'userMessage',
				modalState.initialUserMessage.value,
				false
			);
		}

		await nextTick();
		validateFirstEmptyFieldToFocus();

		// Refresh existing errors without exposing untouched fields during autofill.
		await nextTick();
		await modalState.validate({ mode: 'validated-only' });
	},
	{ immediate: true }
);

let lastOpenedVisitId = props.openHouse?.id;

watch(isOpen, async newValue => {
	if (newValue) {
		// A different visit (or the regular contact CTA) starts its own flow.
		if (
			lastOpenedVisitId !== props.openHouse?.id &&
			!wasOpenedByExternalHandler.value
		) {
			actions.goToContactFormStep();
		}
		lastOpenedVisitId = props.openHouse?.id;

		await nextTick();

		if (
			((modalState.currentStep.value === 'success-message' &&
				!isOpenHouseContext.value) ||
				modalState.currentStep.value === 'submit-error-message') &&
			!wasOpenedByExternalHandler.value
		) {
			actions.goToContactFormStep();
		}

		wasOpenedByExternalHandler.value = false;

		const shouldRespectDefaultExpanded = props.defaultExpanded === false;

		const isExpandableContainerExpanded =
			!shouldRespectDefaultExpanded &&
			(modalState.isUserLoggedIn.value ||
				modalState.shouldExpandContainer.value);

		if (isExpandableContainerExpanded && contactFormSection.value) {
			contactFormSection.value.expandContainer();
			await nextTick();
		}

		validateFirstEmptyFieldToFocus();
		events.addEventContactFormOpened();
	}
});

const handleToggleChange = (isExpanded: boolean) => {};

const otpProvider = ref<OtpProvider>('whatsapp');

const otpProviderName = computed(
	() => strings.otpProviderName[otpProvider.value]
);

const otpDescriptionParts = computed(() => {
	const [before = '', after = ''] =
		strings.phoneOtpDescription[otpProvider.value].split('{0}');
	return { before, after };
});

const otpFallbackParts = computed(() => {
	const [before = '', after = ''] =
		strings.phoneOtpFallbackMessage.split('{0}');
	return { before, after };
});
const isContactFormStep = computed(
	() => modalState.currentStep.value === 'contact-form'
);
const isOtpFormStep = computed(
	() => modalState.currentStep.value === 'otp-form'
);
const isSuccessMessageStep = computed(
	() => modalState.currentStep.value === 'success-message'
);
const isErrorMessageStep = computed(
	() => modalState.currentStep.value === 'submit-error-message'
);

const isOpenHouseSuccess = computed(
	() => isOpenHouseContext.value && isSuccessMessageStep.value
);
const isOpenHouseForm = computed(
	() => isOpenHouseContext.value && isContactFormStep.value
);
const showOpenHouseHeader = computed(
	() => isOpenHouseForm.value || isOpenHouseSuccess.value
);

const isFinalState = computed(
	() => isSuccessMessageStep.value || isErrorMessageStep.value
);

const showCloseIcon = computed(
	() => !isOtpFormStep.value && !showOpenHouseHeader.value
);

const modalContentClass = computed(() =>
	cn(
		'z-[301] border-none w-[90%] md:w-[37.5rem] bg-white text-primary overflow-y-auto no-scrollbar max-h-[95%]',
		{
			'md:max-w-[37.5rem]': !isOtpFormStep.value,
			'w-[calc(100%-2rem)] max-w-[37.5rem] rounded-none sm:rounded-none':
				showOpenHouseHeader.value,
			'max-w-[420px]': isOtpFormStep.value
		}
	)
);

const footerClass = computed(() => [
	'bg-white border-t border-white-dark-3 sticky bottom-0',
	{
		'p-4 md:px-5': isSuccessMessageStep.value,
		'px-5 pb-3 pt-4 md:pb-4': isOpenHouseForm.value,
		'px-4 py-4 md:py-2 flex w-full md:justify-end justify-center':
			(isContactFormStep.value && !isOpenHouseForm.value) || isOtpFormStep.value
	}
]);

const successIconBgClass = computed(() => ({
	'bg-success-light-1': isSuccessMessageStep.value,
	'bg-error-light-1': isErrorMessageStep.value
}));

defineExpose({
	goToOtpFormStep: actions.goToOtpFormStep
});
</script>

<template>
	<ModalPortalModal
		v-model:open="isOpen"
		:show-close-icon="showCloseIcon"
		:prevent-interact-outside="false"
		:content-class="modalContentClass"
		overlay-class="z-[300] bg-black/40"
		header-class="font-matter-sq text-xl md:text-2xl font-normal text-left text-primary p-5 h-[4.5rem] md:h-[4.75rem]"
		:footer-class="footerClass"
	>
		<template #header>
			<div v-if="showOpenHouseHeader" class="flex w-full items-center gap-6">
				<DialogTitle
					class="min-w-0 flex-1 font-matter-sq text-xl font-normal leading-normal tracking-[-0.025rem] md:text-2xl md:tracking-[-0.03rem]"
				>
					{{ modalTitle }}
				</DialogTitle>
				<button
					type="button"
					class="-m-1.5 flex size-11 shrink-0 items-center justify-center focus-visible:outline focus-visible:outline-2"
					aria-label="Fechar"
					@click="handleModalClose(isSuccessMessageStep)"
				>
					<CloseIcon class="size-8 text-primary" aria-hidden="true" />
				</button>
			</div>
			<div v-else-if="isOtpFormStep">
				<DialogTitle
					class="flex items-center gap-4 font-matter-sq text-xl font-normal leading-normal tracking-[-0.03rem] text-primary md:gap-6 md:text-2xl"
				>
					<WestIcon class="cursor-pointer" @click="goToContactFormStep" />
					{{ strings.phoneOtpTitle }}
				</DialogTitle>
			</div>
			<DialogTitle
				v-else
				class="font-matter-sq text-xl font-normal leading-normal tracking-[-0.03rem] lg:text-2xl"
			>
				{{ modalTitle }}
			</DialogTitle>
		</template>

		<template #default>
			<DialogDescription class="hidden" />

			<template v-if="isContactFormStep">
				<OpenHouseVisitSummary
					v-if="isOpenHouseContext"
					class="mx-5 mt-5 md:mx-6 md:mt-6"
					:schedule="openHouseSchedule"
					:location="openHouseLocation"
				/>
				<ContactFormSection
					ref="contactFormSection"
					:class="
						isOpenHouseContext
							? 'px-5 pt-6 md:px-6 md:pt-7'
							: 'px-5 pb-6 pt-5 md:px-6'
					"
					:variant="isOpenHouseContext ? 'open-house' : 'default'"
					:form-data="{
						name: modalState.name.value,
						phone: modalState.phone.value,
						email: modalState.email.value,
						userMessage: modalState.userMessage.value
					}"
					:validation="{
						errors: modalState.errors.value,
						isValid: modalState.meta.value.valid,
						isPhoneDisabled: !!modalState.isPhoneDisabled.value
					}"
					:form-attributes="{
						nameAttrs: modalState.nameAttrs,
						phoneAttrs: modalState.phoneAttrs,
						emailAttrs: modalState.emailAttrs,
						userMessageAttrs: modalState.userMessageAttrs
					}"
					:broker-info="{
						broker: props.broker,
						name: modalState.brokerName.value,
						avatarSrc: brokerSrc,
						message: translateByContext('brokerMessage'),
						atText: translateByContext('brokerAt', {
							brokerCompany: props.companyName
						}),
						companyName: props.companyName,
						companyLogo: props.companyLogo
					}"
					:text-content="{
						intro: isOpenHouseContext ? strings.openHouse.intro : undefined,
						expandText: strings.showMoreInfo,
						collapseText: strings.showLessInfo,
						namePlaceholder: isOpenHouseContext
							? strings.openHouse.namePlaceholder
							: translateByContext('form.name.placeholder'),
						emailPlaceholder: translateByContext('form.email.placeholder'),
						userMessagePlaceholder: translateByContext(
							'form.userMessage.placeholder'
						)
					}"
					:app-state="{
						isPropertyContext: modalState.isPropertyContext.value,
						showBrokerInfo:
							!isOpenHouseContext && modalState.shouldShowBrokerInfo.value,
						isUserLoggedIn: modalState.isUserLoggedIn.value,
						shouldExpandContainer: modalState.shouldExpandContainer.value
					}"
					:analytics-data="{
						propertyCommercialId: props.propertyCommercialId,
						askingPrice: props.askingPrice ?? null,
						videoShorts: props.videoShorts
					}"
					:default-expanded="props.defaultExpanded"
					@update:form-data="handleFormDataUpdate"
					@toggle-expanded="handleToggleChange"
					@submit="handleFormSubmit"
				/>
			</template>

			<div v-else-if="isOtpFormStep" class="px-7 py-5 lg:py-6">
				<div
					class="mb-6 flex flex-col gap-1 text-base font-normal leading-[150%]"
				>
					<p>
						<span>{{ otpDescriptionParts.before }}</span
						><strong class="font-medium">{{ otpProviderName }}</strong
						><span>{{ otpDescriptionParts.after }}</span>
					</p>

					<p
						v-if="otpProvider === 'whatsapp'"
						class="text-sm text-primary-light-3"
					>
						<span>{{ otpFallbackParts.before }}</span
						><strong class="font-medium">SMS</strong
						><span>{{ otpFallbackParts.after }}</span>
					</p>
				</div>

				<OTPForm
					ref="otpFormRef"
					v-model:is-o-t-p-valid="modalState.isOTPValid.value"
					:phone="modalState.phone.value"
					:is-submitting="modalState.isSubmittingOtpForm.value"
					:show-confirm-button="false"
					is-active
					start-countdown-immediately
					@validated="actions.handleOtpFormValidation"
					@change-number="goToContactFormStep"
					@resend="actions.resendOtp"
				/>
			</div>

			<OpenHouseSuccessMessage v-else-if="isOpenHouseSuccess" />

			<div
				v-else-if="isFinalState"
				:data-testid="`${modalState.currentStep.value}-step`"
			>
				<div class="flex flex-col items-center gap-4 px-5 py-8">
					<div
						class="flex size-15 items-center justify-center rounded-full"
						:class="successIconBgClass"
					>
						<CheckCircleIcon
							v-if="isSuccessMessageStep"
							class="size-13 text-success"
						/>
						<ErrorCircleIcon v-else class="size-13 text-error" />
					</div>

					<div class="flex flex-col items-center justify-center gap-3">
						<strong
							class="text-center font-matter-sq text-lg font-normal leading-normal tracking-[-0.0225rem]"
							:data-test-id="isSuccessMessageStep ? 'pdp-success-message' : ''"
						>
							<template v-if="isSuccessMessageStep">
								{{ strings.successConfirmationMessage }}
							</template>
							<template v-else>
								{{ strings.formSubmitErrorMessage }}
							</template>
						</strong>

						<p class="text-center font-sans text-sm font-normal leading-normal">
							<template v-if="isSuccessMessageStep">
								{{
									translateByContext(
										'successConfirmationComplementaryMessage',
										{
											brokerName: modalState.brokerFirstName.value
										}
									)
								}}
							</template>
							<template v-else>
								{{ strings.formSubmitErrorComplementaryMessage }}

								<div
									class="flex w-full flex-col items-center justify-center gap-5 pt-5 md:flex-row"
								>
									<NuxtLink
										v-for="link in contactMethodProps"
										:key="link.label"
										:to="link.route"
										target="_blank"
										@click="handleContactMethodClick(link)"
									>
										<TextLink
											as="p"
											class="flex flex-row items-center justify-center gap-2 text-sm font-normal leading-[150%] text-primary underline decoration-black/20 underline-offset-8"
										>
											<Component :is="link.icon" />
											{{ link.label }}
										</TextLink>
									</NuxtLink>
								</div>
							</template>
						</p>
					</div>

					<div
						v-if="isSuccessMessageStep"
						class="mt-3 flex w-full flex-col items-start gap-2 bg-secondary px-5 py-4 text-left md:px-6 md:py-5"
						data-test-id="pdp-lead-survey-card"
					>
						<div class="flex items-center gap-2 mb-3">
							<Tag
								variant="beige"
								class="bg-white"
								text-class="font-Inter text-xs font-medium leading-[150%] text-primary-light-1"
							>
								<template #icon>
									<ChecklistIcon class="text-primary-light-1" />
								</template>
								{{ strings.leadSurvey.tag }}
							</Tag>
							<Tag
								variant="beige"
								class="bg-white"
								text-class="font-Inter text-xs font-medium leading-[150%] text-primary-light-1"
							>
								<template #icon>
									<ScheduleIcon class="text-primary-light-1" />
								</template>
								{{ strings.leadSurvey.duration }}
							</Tag>
						</div>

						<strong
							class="font-sans text-sm font-medium leading-normal md:text-base md:font-normal"
						>
							{{ strings.leadSurvey.title }}
						</strong>

						<p
							class="font-sans text-xs font-normal leading-normal text-primary-light-1 md:text-sm"
						>
							{{ strings.leadSurvey.description }}
						</p>

						<TextLink
							as="button"
							type="button"
							class="flex items-center mt-3 gap-2 text-sm font-medium leading-[150%] text-primary underline decoration-black/20 underline-offset-4"
							data-test-id="pdp-lead-survey-cta"
							@click="openLeadSurveyTypeform"
						>
							<RecordVoiceOverIcon class="size-5 text-primary" />
							{{ strings.leadSurvey.cta }}
						</TextLink>
					</div>
				</div>
			</div>

			<div
				v-if="isContactFormStep"
				:class="
					isOpenHouseContext
						? 'bg-white px-5 pb-4 pt-6 text-left text-xs font-normal leading-normal text-primary-light-1 md:px-6 md:pt-8'
						: 'bg-white py-6 pl-5 pt-1 text-left text-xs font-normal leading-4.5 text-primary-light-1'
				"
			>
				<PrivacyTermsStatement
					v-if="isOpenHouseContext"
					:messages="openHouseMobilePrivacyMessages"
					class="md:hidden"
					text-class="flex min-h-11 flex-wrap content-start gap-1 leading-normal"
					link-class="underline decoration-primary-light-1 underline-offset-[0.2rem]"
					@terms-click="events.addEventsTermsOrPolicy('terms')"
					@privacy-click="events.addEventsTermsOrPolicy('policy')"
				/>
				<PrivacyTermsStatement
					:messages="isOpenHouseContext ? openHousePrivacyMessages : undefined"
					:class="isOpenHouseContext ? 'hidden md:block' : undefined"
					:text-class="
						isOpenHouseContext ? 'leading-normal' : 'flex flex-wrap gap-1'
					"
					link-class="underline decoration-primary-light-1 underline-offset-[0.2rem]"
					@terms-click="events.addEventsTermsOrPolicy('terms')"
					@privacy-click="events.addEventsTermsOrPolicy('policy')"
				/>
			</div>
		</template>

		<template #footer>
			<div
				v-if="isContactFormStep"
				class="w-full md:w-auto"
				:class="{ 'md:ml-auto': isOpenHouseContext }"
			>
				<Button
					size="md"
					class="w-full text-base font-medium"
					:class="{ 'h-[50px] px-8': isOpenHouseContext }"
					:disabled="
						!modalState.meta.value.valid || modalState.isSubmittingForm.value
					"
					data-test-id="pdp-send-contact-form-button"
					@click="handleFormSubmit"
				>
					<IconLineMdLoadingTwotoneLoop
						v-if="modalState.isSubmittingForm.value"
						class="size-[1.7rem]"
					/>
					<template v-else>
						{{
							isOpenHouseContext
								? strings.openHouse.sendButtonText
								: translateByContext('form.sendButtonText')
						}}
						<NorthEastIcon class="size-5 text-beige" aria-hidden="true" />
					</template>
				</Button>
			</div>

			<div v-else-if="isOtpFormStep" class="w-full">
				<Button
					:disabled="modalState.isSubmittingOtpForm.value"
					class="h-12 w-full text-base font-medium"
					data-testid="submit-confirmation-button"
					@click="otpFormRef?.submitCode"
				>
					<IconLineMdLoadingTwotoneLoop
						v-if="modalState.isSubmittingOtpForm.value"
						class="size-[1.7rem]"
					/>

					<template v-else>
						{{ strings.confirmButton }}
					</template>
				</Button>
			</div>

			<div
				v-else-if="isSuccessMessageStep"
				:class="
					isOpenHouseSuccess
						? 'flex w-full flex-col gap-3 md:flex-row md:justify-end'
						: 'flex w-full flex-col gap-3 lg:flex-row lg:justify-end'
				"
			>
				<Button
					v-if="props.locationSlug || isOpenHouseSuccess"
					size="md"
					:class="
						isOpenHouseSuccess
							? 'h-11 w-full shrink-0 px-4 py-2 text-sm font-medium leading-5 md:h-12 md:w-auto md:px-5 md:py-3 md:text-base md:leading-[26px]'
							: 'ml-auto flex gap-2 text-base font-medium md:gap-3 lg:w-fit'
					"
					variant="secondary"
					@click="goToSimilarPropertiesOnListing"
				>
					{{
						isOpenHouseSuccess
							? strings.openHouse.similarProperties
							: strings.similarProperties
					}}
				</Button>
				<Button
					size="md"
					:class="
						isOpenHouseSuccess
							? 'h-11 w-full shrink-0 gap-2 px-4 py-2 text-sm font-medium leading-5 md:h-12 md:w-auto md:gap-3 md:px-5 md:py-3 md:text-base md:leading-[26px]'
							: 'ml-auto flex gap-2 text-base font-medium md:gap-3'
					"
					@click="closeModalWithScroll"
				>
					<CheckIcon
						v-if="isOpenHouseSuccess"
						class="size-5 text-beige"
						aria-hidden="true"
					/>
					<DoneIcon v-else class="size-5 text-beige" />
					{{ strings.successConfirmationButtonText }}
				</Button>
			</div>

			<Button
				v-else-if="isErrorMessageStep"
				size="md"
				class="ml-auto flex gap-2 text-base font-medium md:w-fit md:gap-3"
				@click="goToContactFormStep"
			>
				{{ strings.retryButtonText }}
			</Button>
		</template>
	</ModalPortalModal>
</template>
